'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useState } from 'react';

import {
  closeResponsiveDialogWithoutHistory,
  requestCloseResponsiveDialog,
  RESPONSIVE_DIALOG_HISTORY_KEY,
  useResponsiveDialogStore,
} from '@/stores/DrawerStore';

const ResponsiveDialog = dynamic(
  () =>
    import('../../organism/ResponsiveDialog').then((mod) => ({
      default: mod.ResponsiveDialog,
    })),
  { ssr: false },
);

/**
 * 데스크톱에서는 모달, 모바일에서는 드로워로 표시\
 * openResponsiveDialog를 이용해서 간결하게 사용 가능
 *
 * @example
 *
 * ```tsx
 * import { openResponsiveDialog } from '@/stores/DrawerStore';
 *
 * openResponsiveDialog({
 *   component: HouseholdConsent,
 * });
 * ```
 */
export const ResponsiveDialogContainer = () => {
  const { currentDialog, isOpen } = useResponsiveDialogStore();
  const [isMounted, setIsMounted] = useState(false);
  const shouldUseHistory = isOpen && !!currentDialog;

  const handleCloseWithoutHistory = useCallback(() => {
    // popstate 진입점 전용 close.
    // history는 이미 browser가 한 칸 이동시킨 상태이므로 여기서 다시 back하지 않는다.
    closeResponsiveDialogWithoutHistory();
  }, []);

  const requestClose = useCallback(() => {
    requestCloseResponsiveDialog();
  }, []);

  // 클라이언트 사이드에서만 Portal을 렌더링하기 위한 마운트 체크
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // ESC 키로 Drawer 닫기
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        requestClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, requestClose]);

  // 브라우저 뒤로가기 버튼으로 ResponsiveDialog 닫기
  useEffect(() => {
    if (!shouldUseHistory) return;

    // ResponsiveDialog가 열릴 때 현재 Next.js history state를 보존한 채 marker만 추가한다.
    // X/ESC/backdrop/swipe close는 이 entry를 history.back()으로 소비하고,
    // browser back은 popstate listener에서 공통 상태를 닫는다.
    const state = {
      ...window.history.state,
      [RESPONSIVE_DIALOG_HISTORY_KEY]: true,
    };
    window.history.pushState(state, '');

    const handlePopState = (event: PopStateEvent) => {
      // 뒤로가기 시 ResponsiveDialog 닫기
      if (useResponsiveDialogStore.getState().isOpen) {
        event.preventDefault();
        handleCloseWithoutHistory();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [shouldUseHistory, handleCloseWithoutHistory]);

  if (!isMounted || !currentDialog) return null;

  const Component = currentDialog.component;

  return (
    <ResponsiveDialog
      opened={isOpen}
      onClose={requestClose}
      title={currentDialog.title}
      duration={currentDialog.duration}
      className={currentDialog.className}
      modalHeader={currentDialog.modalHeader}
      drawerNoPadding={currentDialog.drawerNoPadding}
      hideDrawerHeader={currentDialog.hideDrawerHeader}
      drawerType={currentDialog.drawerType}
      forceModal={currentDialog.forceModal}
    >
      <Component {...currentDialog.props} onClose={requestClose} />
    </ResponsiveDialog>
  );
};
